import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, addDoc, query, orderBy, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebase';
import { useAuth, useEditMode } from '../AuthContext';
import { fetchEnrichedRoster, EnrichedRosterMember } from '../services/rosterService';
import {
  API_CONFIG,
  displayWeeksMetRequirement,
  getApiUrl,
  GUILD_REGION,
  profileRealmMatchesGuildRow,
} from '../config/apiConfig';

// Helper to generate the correct spec icon path (all lowercase, no spaces or hyphens)
function toSpecIconPath(className: string, spec: string, ext: string = 'png') {
  return `/images/spec/${className.toLowerCase().replace(/ |-/g, '')}/${spec.toLowerCase().replace(/ |-/g, '')}.${ext}`;
}
// SpecIcon component to handle .png/.jpg fallback
const SpecIcon: React.FC<{ className: string; spec: string }> = ({ className, spec }) => {
  const [imgSrc, setImgSrc] = React.useState(toSpecIconPath(className, spec, 'webp'));
  return (
    <img
      src={imgSrc}
      alt={`${spec} icon`}
      className="w-5 h-5 inline-block"
      onError={e => {
        if (imgSrc.endsWith('.webp')) {
          setImgSrc(toSpecIconPath(className, spec, 'png'));
        } else if (imgSrc.endsWith('.png')) {
          setImgSrc(toSpecIconPath(className, spec, 'jpg'));
        } else {
          (e.target as HTMLImageElement).style.display = 'none';
        }
      }}
    />
  );
};

const Roster: React.FC = () => {
  const { user } = useAuth();
  const { editMode } = useEditMode();
  const [rosterLoading, setRosterLoading] = useState(true);

  // Tracking state
  const [tracked, setTracked] = useState<string[]>([]); // composite key: name|realm|region
  const [trackLoading, setTrackLoading] = useState<string | null>(null);

  // Leadership state
  const [leaders, setLeaders] = useState<any[]>([]);
  const [leadershipLoading, setLeadershipLoading] = useState(true);
  const [leaderForm, setLeaderForm] = useState({ name: '', role: '', class: '', spec: '', order: 1, weeksMetRequirement: 0 });
  const [leaderError, setLeaderError] = useState('');
  const [leaderAdding, setLeaderAdding] = useState(false);
  const [leaderEditId, setLeaderEditId] = useState<string | null>(null);
  const [leaderDeleteId, setLeaderDeleteId] = useState<string | null>(null);
  const [showLeaderForm, setShowLeaderForm] = useState(false);

  // API roster state
  const [mythicTeam, setMythicTeam] = useState<EnrichedRosterMember[]>([]);
  const [heroicTeam, setHeroicTeam] = useState<EnrichedRosterMember[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<'mythic' | 'heroic'>('mythic');
  const [apiError, setApiError] = useState<string | null>(null);

  // WoW class/spec options (copied from Recruitment.tsx)
  const wowClasses: Record<string, string[]> = {
    'Death Knight': ['Blood', 'Frost', 'Unholy'],
    'Demon Hunter': ['Havoc', 'Vengeance', 'Devourer'],
    'Druid': ['Balance', 'Feral', 'Guardian', 'Restoration'],
    'Evoker': ['Augmentation', 'Devastation', 'Preservation'],
    'Hunter': ['Beast Mastery', 'Marksmanship', 'Survival'],
    'Mage': ['Arcane', 'Fire', 'Frost'],
    'Monk': ['Brewmaster', 'Mistweaver', 'Windwalker'],
    'Paladin': ['Holy', 'Protection', 'Retribution'],
    'Priest': ['Discipline', 'Holy', 'Shadow'],
    'Rogue': ['Assassination', 'Outlaw', 'Subtlety'],
    'Shaman': ['Elemental', 'Enhancement', 'Restoration'],
    'Warlock': ['Affliction', 'Demonology', 'Destruction'],
    'Warrior': ['Arms', 'Fury', 'Protection'],
  };
  const classOptions = Object.keys(wowClasses);
  const leaderSpecOptions = leaderForm.class ? wowClasses[leaderForm.class] : [];

  useEffect(() => {
    setRosterLoading(true);
    setApiError(null);
    
    const fetchRosters = async () => {
      try {
        const result = await fetchEnrichedRoster();
        
        if (result.error) {
          setApiError(result.error);
        } else {
          setMythicTeam(result.mythic);
          setHeroicTeam(result.heroic);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch roster data';
        setApiError(errorMessage);
      } finally {
        setRosterLoading(false);
      }
    };
    
    fetchRosters();
  }, []);

  // Fetch leadership from Firestore and enrich with avatar data and weekly counts
  useEffect(() => {
    const fetchLeaders = async () => {
      setLeadershipLoading(true);
      const db = getFirestore(app);
      const q = query(collection(db, 'leadership'), orderBy('order'));
      const querySnapshot = await getDocs(q);
      const leadersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Set leaders immediately with basic data
      setLeaders(leadersData);
      setLeadershipLoading(false);
      
      // Then enrich with avatar data and weekly counts in the background (non-blocking)
      try {
        // First fetch all weekly counts in one API call
        const countsUrl = getApiUrl('guild-mythic-counts');
        
        let weeklyCounts = new Map<string, number>();
        let weeksMetRequirement = new Map<string, number>();
        try {
          const countsResponse = await fetch(countsUrl, {
            headers: { 'X-API-Key': API_CONFIG.API_KEY },
            method: 'GET',
            mode: 'cors',
            signal: AbortSignal.timeout(30000), // 30 second timeout
          });
          
          if (countsResponse.ok) {
            const countsData = await countsResponse.json();
            if (countsData.data && Array.isArray(countsData.data)) {
              countsData.data.forEach((player: any) => {
                if (player.Name) {
                  const key = player.Name.toLowerCase();
                  // Store weekly count (capped at 8)
                  if (player.count !== undefined) {
                    weeklyCounts.set(key, Math.min(player.count, 8));
                  }
                  // Store weeks met requirement
                  if (player.weeks_with_4_plus_runs !== undefined) {
                    weeksMetRequirement.set(key, displayWeeksMetRequirement(player.weeks_with_4_plus_runs));
                  }
                }
              });
            }
          }
        } catch (countsError) {
          console.warn('Failed to fetch weekly counts for leadership:', countsError);
        }
        
        const enrichedLeaders = await Promise.all(
          leadersData.map(async (leader: any) => {
            try {
              const leaderRealm = typeof leader.realm === 'string' ? leader.realm : '';
              const url = getApiUrl(
                `character-profiles/${encodeURIComponent(leader.name)}?realm=${encodeURIComponent(leaderRealm)}&region=${encodeURIComponent(GUILD_REGION)}`
              );

              const response = await fetch(url, {
                headers: { 'X-API-Key': API_CONFIG.API_KEY },
                mode: 'cors',
                signal: AbortSignal.timeout(30000), // 30 second timeout
              });

              if (response.ok) {
                const data = await response.json();
                const prof = data.data;
                if (prof && profileRealmMatchesGuildRow(prof.realm, leaderRealm)) {
                  const weeklyCount = weeklyCounts.get(leader.name.toLowerCase()) ?? 0;
                  const weeksMet = weeksMetRequirement.get(leader.name.toLowerCase()) ?? 0;

                  return {
                    ...leader,
                    avatar: prof.avatar_url || prof.avatar || prof.character_image || null,
                    mythicPlusThisWeek: weeklyCount,
                    weeksMetRequirement: weeksMet,
                  };
                }
              }
              
              // Get weekly count and weeks met requirement from the pre-fetched maps even if avatar fetch fails
              const weeklyCount = weeklyCounts.get(leader.name.toLowerCase()) ?? 0;
              const weeksMet = weeksMetRequirement.get(leader.name.toLowerCase()) ?? 0;
              return {
                ...leader,
                avatar: null,
                mythicPlusThisWeek: weeklyCount,
                weeksMetRequirement: weeksMet,
              };
            } catch (error) {
              console.warn(`Failed to fetch avatar for ${leader.name}:`, error);
            }
            
            // Return with weekly count and weeks met requirement even if avatar fetch fails
            const weeklyCount = weeklyCounts.get(leader.name.toLowerCase()) ?? 0;
            const weeksMet = weeksMetRequirement.get(leader.name.toLowerCase()) ?? 0;
            return {
              ...leader,
              avatar: null,
              mythicPlusThisWeek: weeklyCount,
              weeksMetRequirement: weeksMet,
            };
          })
        );
        
        setLeaders(enrichedLeaders);
      } catch (error) {
        console.error('Failed to enrich leaders with avatar data:', error);
        // Keep the basic data if enrichment fails
      }
    };
    fetchLeaders();
  }, []);

  // Fetch tracked characters for user
  const fetchTracked = async (userId: string) => {
    const db = getFirestore(app);
    const userDoc = await getDoc(doc(db, 'users', userId));
    const trackedList = userDoc.exists() && userDoc.data().trackedCharacters ? userDoc.data().trackedCharacters : [];
    setTracked(trackedList.map((c: any) => `${c.name}|${c.realm}|${c.region}`));
  };

  useEffect(() => {
    if (user) {
      fetchTracked(user.uid);
    }
  }, [user]);

  // Track a character
  const handleTrack = async (name: string, realm: string, region: string) => {
    if (!user) return;
    setTrackLoading(`${name}|${realm}|${region}`);
    const db = getFirestore(app);
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    const trackedList = userDoc.exists() && userDoc.data().trackedCharacters ? userDoc.data().trackedCharacters : [];
    if (!trackedList.some((c: any) => c.name === name && c.realm === realm && c.region === region)) {
      const newList = [...trackedList, { name, realm, region }];
      await updateDoc(userRef, { trackedCharacters: newList });
    }
    await fetchTracked(user.uid);
    setTrackLoading(null);
  };

  // Untrack a character
  const handleUntrack = async (name: string, realm: string, region: string) => {
    if (!user) return;
    setTrackLoading(`${name}|${realm}|${region}`);
    const db = getFirestore(app);
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    const trackedList = userDoc.exists() && userDoc.data().trackedCharacters ? userDoc.data().trackedCharacters : [];
    const newList = trackedList.filter((c: any) => !(c.name === name && c.realm === realm && c.region === region));
    await updateDoc(userRef, { trackedCharacters: newList });
    await fetchTracked(user.uid);
    setTrackLoading(null);
  };

  // Old CRUD handlers removed - roster now managed via API
  // Old edit functions removed - roster now managed via API
  // Delete/edit handlers removed - roster data now comes from API

  // Leadership CRUD handlers
  const handleLeaderFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target as any;
    setLeaderForm({ ...leaderForm, [name]: value });
  };

  const handleAddLeader = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leaderForm.name || !leaderForm.role || !leaderForm.class || !leaderForm.spec) {
      setLeaderError('Please fill in all fields.');
      return;
    }
    setLeaderError('');
    setLeaderAdding(true);
    const db = getFirestore(app);
    await addDoc(collection(db, 'leadership'), {
      name: leaderForm.name,
      role: leaderForm.role,
      class: leaderForm.class,
      spec: leaderForm.spec,
      order: Number(leaderForm.order),
      weeksMetRequirement: Number(leaderForm.weeksMetRequirement) || 0,
    });
    setLeaderForm({ name: '', role: '', class: '', spec: '', order: 1, weeksMetRequirement: 0 });
    setShowLeaderForm(false);
    // Refresh leaders
    const q = query(collection(db, 'leadership'), orderBy('order'));
    const querySnapshot = await getDocs(q);
    setLeaders(querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })));
    setLeaderAdding(false);
  };

  const startEditLeader = (leader: any) => {
    setLeaderEditId(leader.id);
    setLeaderForm({ ...leader, order: leader.order, weeksMetRequirement: leader.weeksMetRequirement ?? 0 });
    setShowLeaderForm(true);
  };

  const handleEditLeader = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leaderForm.name || !leaderForm.role || !leaderForm.class || !leaderForm.spec) {
      setLeaderError('Please fill in all fields.');
      return;
    }
    setLeaderError('');
    setLeaderAdding(true);
    const db = getFirestore(app);
    await updateDoc(doc(db, 'leadership', leaderEditId!), {
      name: leaderForm.name,
      role: leaderForm.role,
      class: leaderForm.class,
      spec: leaderForm.spec,
      order: Number(leaderForm.order),
      weeksMetRequirement: Number(leaderForm.weeksMetRequirement) || 0,
    });
    setLeaderForm({ name: '', role: '', class: '', spec: '', order: 1, weeksMetRequirement: 0 });
    setLeaderEditId(null);
    setShowLeaderForm(false);
    // Refresh leaders
    const q = query(collection(db, 'leadership'), orderBy('order'));
    const querySnapshot = await getDocs(q);
    setLeaders(querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })));
    setLeaderAdding(false);
  };

  const handleDeleteLeader = async (id: string) => {
    setLeaderAdding(true);
    const db = getFirestore(app);
    await deleteDoc(doc(db, 'leadership', id));
    // Refresh leaders
    const q = query(collection(db, 'leadership'), orderBy('order'));
    const querySnapshot = await getDocs(q);
    setLeaders(querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })));
    setLeaderAdding(false);
    setLeaderDeleteId(null);
  };

  return (
    <div className="page-container">
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
          <span className="gradient-text">Guild Roster</span>
        </h1>
        {user && user.role === 'admin' && editMode && (
          <div className="mb-6 flex flex-col items-center">
            <div className="flex gap-6 mb-2">
              <button
                className="text-cyan-400 underline hover:text-cyan-300 font-semibold transition text-lg"
                onClick={() => { setShowLeaderForm(true); setLeaderEditId(null); setLeaderForm({ name: '', role: '', class: '', spec: '', order: leaders.length + 1, weeksMetRequirement: 0 }); }}
              >
                Add Leader
              </button>
              <div className="text-center">
                <div className="text-yellow-400 text-sm italic mb-2">
                  Roster data is now automatically synced from the guild API
                </div>
                <div className="text-xs text-gray-400 space-y-1">
                  <div>📋 <strong>Main Roster Criteria:</strong> Level 80 only</div>
                  <div>⚔️ <strong>Mythic Team:</strong> Guild Master (1) + Officer (1) + Mythic Raiders (~45)</div>
                  <div>🛡️ <strong>Heroic Team:</strong> Heroic Raiders (~22)</div>
                  <div>⚠️ <strong>Note:</strong> Officer alts and general members excluded</div>
                  <div>✅ <strong>Source:</strong> All 401 guild members from API</div>
                </div>
              </div>
            </div>
            {/* Add form removed - roster now managed via API */}
          </div>
        )}
        {/* Officers Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-center">Leadership</h2>
          {leadershipLoading ? (
            <div className="text-center text-text-secondary">Loading leadership...</div>
          ) : (
            <div className="space-y-6">
              {/* First row - 4 cards for leadership */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {leaders.slice(0, 4).map((leader) => (
                  <div key={leader.id} className="glass p-4 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      {leader.avatar ? (
                        <div className="avatar-container">
                          <img 
                            src={leader.avatar} 
                            alt={`${leader.name} avatar`}
                            className="w-12 h-12 rounded-full object-cover avatar-image"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-full flex items-center justify-center avatar-fallback">
                          <span className="text-cyan-400 text-lg font-bold">
                            {leader.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold">{leader.name}</h3>
                        <p className="text-text-secondary text-sm flex items-center gap-2">
                          {leader.class} - <SpecIcon className={leader.class} spec={leader.spec} /> {leader.spec}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-cyan-400">{leader.region?.toUpperCase() || 'US'} - {leader.realm || 'Stormrage'}</p>
                    <span className="text-xs neon-cyan-text">{leader.role}</span>
                    {/* Mythic+ Counters for Leadership */}
                    <div className="flex flex-col items-start mt-2">
                      <span className="text-sm text-cyan-300 font-semibold">Mythic+ This Week: {leader.mythicPlusThisWeek ?? 0}/8</span>
                      <span className="text-sm text-cyan-300">Weeks Met Minimum: {leader.weeksMetRequirement ?? 0}</span>
                    </div>
                    {user && !editMode && (
                      <div className="mt-2">
                        {tracked.includes(`${leader.name}|${leader.realm}|${leader.region}`) ? (
                          <button
                            className="text-red-400 hover:text-red-600"
                            onClick={() => handleUntrack(leader.name, leader.realm, leader.region)}
                            disabled={trackLoading === `${leader.name}|${leader.realm}|${leader.region}`}
                          >
                            {trackLoading === `${leader.name}|${leader.realm}|${leader.region}` ? 'Removing...' : 'Untrack'}
                          </button>
                        ) : (
                          <button
                            className="text-cyan-400 hover:text-cyan-600"
                            onClick={() => handleTrack(leader.name, leader.realm, leader.region)}
                            disabled={trackLoading === `${leader.name}|${leader.realm}|${leader.region}`}
                          >
                            {trackLoading === `${leader.name}|${leader.realm}|${leader.region}` ? 'Tracking...' : 'Track'}
                          </button>
                        )}
                      </div>
                    )}
                    {user && user.role === 'admin' && editMode && (
                      <div className="flex gap-2 mt-2">
                        <button className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600" onClick={() => startEditLeader(leader)}>Edit</button>
                        <button className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600" onClick={() => setLeaderDeleteId(leader.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Second row - 3 cards centered */}
              {leaders.length > 4 && (
                <div className="flex justify-center">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{width: '75%'}}>
                    {leaders.slice(4).map((leader) => (
                      <div key={leader.id} className="glass p-4 rounded-lg">
                        <div className="flex items-center gap-3 mb-2">
                          {leader.avatar ? (
                            <div className="avatar-container">
                              <img 
                                src={leader.avatar} 
                                alt={`${leader.name} avatar`}
                                className="w-12 h-12 rounded-full object-cover avatar-image"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-full flex items-center justify-center avatar-fallback">
                              <span className="text-cyan-400 text-lg font-bold">
                                {leader.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold">{leader.name}</h3>
                            <p className="text-text-secondary text-sm flex items-center gap-2">
                              {leader.class} - <SpecIcon className={leader.class} spec={leader.spec} /> {leader.spec}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-cyan-400">{leader.region?.toUpperCase() || 'US'} - {leader.realm || 'Stormrage'}</p>
                        <span className="text-xs neon-cyan-text">{leader.role}</span>
                                                {/* Mythic+ Counters for Leadership */}
                        <div className="flex flex-col items-start mt-2">
                          <span className="text-sm text-cyan-300 font-semibold">Mythic+ This Week: {leader.mythicPlusThisWeek ?? 0}/8</span>
                          <span className="text-sm text-cyan-300">Weeks Met Minimum: {leader.weeksMetRequirement ?? 0}</span>
                        </div>
                        {user && !editMode && (
                          <div className="mt-2">
                            {tracked.includes(`${leader.name}|${leader.realm}|${leader.region}`) ? (
                              <button
                                className="text-red-400 hover:text-red-600"
                                onClick={() => handleUntrack(leader.name, leader.realm, leader.region)}
                                disabled={trackLoading === `${leader.name}|${leader.realm}|${leader.region}`}
                              >
                                {trackLoading === `${leader.name}|${leader.realm}|${leader.region}` ? 'Removing...' : 'Untrack'}
                              </button>
                            ) : (
                              <button
                                className="text-cyan-400 hover:text-cyan-600"
                                onClick={() => handleTrack(leader.name, leader.realm, leader.region)}
                                disabled={trackLoading === `${leader.name}|${leader.realm}|${leader.region}`}
                              >
                                {trackLoading === `${leader.name}|${leader.realm}|${leader.region}` ? 'Tracking...' : 'Track'}
                              </button>
                            )}
                          </div>
                        )}
                        {user && user.role === 'admin' && editMode && (
                          <div className="flex gap-2 mt-2">
                            <button className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600" onClick={() => startEditLeader(leader)}>Edit</button>
                            <button className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600" onClick={() => setLeaderDeleteId(leader.id)}>Delete</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Leader Add/Edit Modal */}
        {showLeaderForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={() => { setShowLeaderForm(false); setLeaderEditId(null); setLeaderForm({ name: '', role: '', class: '', spec: '', order: leaders.length + 1, weeksMetRequirement: 0 }); }}>
            <div className="relative max-w-md w-full mx-4 bg-surface p-8 rounded-xl" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-4 text-center">{leaderEditId ? 'Edit Leader' : 'Add Leader'}</h2>
              <form onSubmit={leaderEditId ? handleEditLeader : handleAddLeader} className="space-y-4">
                {leaderError && <div className="text-red-400 text-center mb-2">{leaderError}</div>}
                <div>
                  <label className="block mb-1 font-medium">Name</label>
                  <input type="text" name="name" value={leaderForm.name} onChange={handleLeaderFormChange} className="w-full p-2 rounded bg-surface-light border border-border" required />
                </div>
                <div>
                  <label className="block mb-1 font-medium">Role</label>
                  <input type="text" name="role" value={leaderForm.role} onChange={handleLeaderFormChange} className="w-full p-2 rounded bg-surface-light border border-border" required />
                </div>
                <div>
                  <label className="block mb-1 font-medium">Class</label>
                  <select name="class" value={leaderForm.class} onChange={handleLeaderFormChange} className="w-full p-2 rounded bg-surface-light border border-border" required>
                    <option value="">Select a class</option>
                    {classOptions.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 font-medium">Spec</label>
                  <select name="spec" value={leaderForm.spec} onChange={handleLeaderFormChange} className="w-full p-2 rounded bg-surface-light border border-border" required disabled={!leaderForm.class}>
                    <option value="">{leaderForm.class ? 'Select a spec' : 'Select a class first'}</option>
                    {leaderSpecOptions.map(spec => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 font-medium">Order</label>
                  <input type="number" name="order" value={leaderForm.order} onChange={handleLeaderFormChange} className="w-full p-2 rounded bg-surface-light border border-border" min={1} required />
                </div>
                <div>
                  <label className="block mb-1 font-medium">Weeks Met Minimum</label>
                  <input type="number" name="weeksMetRequirement" value={leaderForm.weeksMetRequirement} onChange={handleLeaderFormChange} className="w-full p-2 rounded bg-surface-light border border-border" min={0} required />
                </div>
                <div className="flex justify-center gap-4 mt-6">
                  <button type="submit" className="btn-neon px-6 py-2 rounded font-bold" disabled={leaderAdding}>{leaderAdding ? (leaderEditId ? 'Saving...' : 'Adding...') : (leaderEditId ? 'Save' : 'Add')}</button>
                  <button type="button" className="btn px-6 py-2 rounded font-bold" onClick={() => { setShowLeaderForm(false); setLeaderEditId(null); setLeaderForm({ name: '', role: '', class: '', spec: '', order: leaders.length + 1, weeksMetRequirement: 0 }); }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Leader Delete Modal */}
        {leaderDeleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={() => setLeaderDeleteId(null)}>
            <div className="relative max-w-md w-full mx-4 bg-surface p-8 rounded-xl" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-4 text-center text-red-400">Delete Leader</h2>
              <p className="text-center mb-6 text-gray-300">
                Are you sure you want to delete this leader? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded font-bold"
                  onClick={() => {
                    const leader = leaders.find(l => l.id === leaderDeleteId);
                    if (leader) handleDeleteLeader(leaderDeleteId);
                  }}
                  disabled={leaderAdding}
                >
                  {leaderAdding ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  className="btn px-6 py-2 rounded font-bold"
                  onClick={() => setLeaderDeleteId(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Team Toggle Buttons */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            className={`px-6 py-2 rounded font-bold transition-all ${selectedTeam === 'mythic' ? 'bg-cyan-600 text-white' : 'bg-surface text-cyan-600 border border-cyan-600'}`}
            onClick={() => setSelectedTeam('mythic')}
          >
            Mythic Team
          </button>
          <button
            className={`px-6 py-2 rounded font-bold transition-all ${selectedTeam === 'heroic' ? 'bg-cyan-600 text-white' : 'bg-surface text-cyan-600 border border-cyan-600'}`}
            onClick={() => setSelectedTeam('heroic')}
          >
            Heroic Team
          </button>
        </div>
        {/* Roster Section */}
        {rosterLoading ? (
          <div className="text-center text-text-secondary">Loading roster from API...</div>
        ) : apiError ? (
          <div className="text-center text-red-400">
            <p>Error loading roster: {apiError}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-2 px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(selectedTeam === 'mythic' ? mythicTeam : heroicTeam).map((member) => (
              <div key={member.id || member.name} className="glass p-4 rounded-lg">
                {/* Edit form removed - data comes from API */}
                {true && (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      {member.avatar ? (
                        <div className="avatar-container">
                          <img 
                            src={member.avatar} 
                            alt={`${member.name} avatar`}
                            className="w-10 h-10 rounded-full object-cover avatar-image"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full flex items-center justify-center avatar-fallback">
                          <span className="text-cyan-400 text-sm font-bold">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold">{member.name}</h3>
                          <span className="text-xs neon-cyan-text">{member.rank}</span>
                        </div>
                        <p className="text-text-secondary text-sm flex items-center gap-2">
                          {member.class || 'Unknown Class'} - <SpecIcon className={member.class || ''} spec={member.spec || ''} /> {member.spec || 'Unknown Spec'}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-cyan-400">{member.region?.toUpperCase() || 'US'} - {member.realm || 'Unknown'}</p>
                                         {/* Mythic+ Counters for Raiders */}
                     <div className="flex flex-col items-start mt-2">
                       <span className="text-sm text-cyan-300 font-semibold">Mythic+ This Week: {member.mythicPlusThisWeek ?? 0}/8</span>
                       {selectedTeam === 'mythic' && (
                         <span className="text-sm text-cyan-300">Weeks Met Minimum: {member.weeksMetRequirement ?? 0}</span>
                       )}
                       {selectedTeam === 'heroic' && (
                         <span className="text-xs text-cyan-200 italic">(Optional)</span>
                       )}
                     </div>
                    {user && !editMode && (
                      <div className="mt-2">
                        {tracked.includes(`${member.name}|${member.realm}|${member.region}`) ? (
                          <button
                            className="text-red-400 hover:text-red-600"
                            onClick={() => handleUntrack(member.name, member.realm, member.region)}
                            disabled={trackLoading === `${member.name}|${member.realm}|${member.region}`}
                          >
                            {trackLoading === `${member.name}|${member.realm}|${member.region}` ? 'Removing...' : 'Untrack'}
                          </button>
                        ) : (
                          <button
                            className="text-cyan-400 hover:text-cyan-600"
                            onClick={() => handleTrack(member.name, member.realm, member.region)}
                            disabled={trackLoading === `${member.name}|${member.realm}|${member.region}`}
                          >
                            {trackLoading === `${member.name}|${member.realm}|${member.region}` ? 'Tracking...' : 'Track'}
                          </button>
                        )}
                      </div>
                    )}
                    {user && user.role === 'admin' && editMode && (
                      <div className="flex gap-2 mt-2">
                        <div className="text-xs text-yellow-400 italic">
                          Roster data is now managed via API
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Delete confirmation removed - roster now managed via API */}
    </div>
  );
};

export default Roster; 